/** 预设场景模板（F-002）：一键加载完整场景与客流配置。 */
import type { SetupFields } from '../ui/store';

/** 模板携带的草稿字段（不含 store 方法）。 */
export type SetupDraft = SetupFields;

export interface Template {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly setup: SetupFields;
}

const std = {
  capacity: 13, ratedSpeed: 2.5, acceleration: 1.0,
  doorOpenTime: 2000, doorCloseTime: 3000, boardTimePerPax: 800, alightTimePerPax: 600,
  carMass: 1500, initialFloor: 0,
};

export const TEMPLATES: readonly Template[] = [
  {
    id: 'residential-morning',
    name: '住宅早高峰',
    description: '低层住宅，少量低速梯，早间以首层上行为主。',
    setup: {
      floors: 8, floorHeight: 2.8, lobbyFloor: 0, elevatorCount: 2,
      elevator: { ...std, capacity: 10, ratedSpeed: 1.5 },
      segments: [{ startSec: 0, endSec: 3600, arrivalRatePer5min: 30 }],
      upPeak: 0.7, downPeak: 0.2, interfloor: 0.1,
      seed: 7, strategyId: 'nearest-car', strategyParams: {}, rules: [],
    },
  },
  {
    id: 'office-all-day',
    name: '办公楼全日',
    description: '中层办公楼，含早高峰上行、晚间下行与午间层间交通。',
    setup: {
      floors: 12, floorHeight: 3, lobbyFloor: 0, elevatorCount: 3,
      elevator: { ...std },
      segments: [
        { startSec: 0, endSec: 25200, arrivalRatePer5min: 15 },
        { startSec: 25200, endSec: 28800, arrivalRatePer5min: 80 },
        { startSec: 28800, endSec: 36000, arrivalRatePer5min: 30 },
      ],
      upPeak: 0.45, downPeak: 0.45, interfloor: 0.1,
      seed: 42, strategyId: 'cost-group', strategyParams: {}, rules: [],
    },
  },
  {
    id: 'hotel',
    name: '酒店',
    description: '中高层酒店，大轿厢低速，三向交通较均衡。',
    setup: {
      floors: 10, floorHeight: 3.2, lobbyFloor: 0, elevatorCount: 2,
      elevator: { ...std, capacity: 15, ratedSpeed: 2.0 },
      segments: [{ startSec: 0, endSec: 3600, arrivalRatePer5min: 25 }],
      upPeak: 0.4, downPeak: 0.3, interfloor: 0.3,
      seed: 11, strategyId: 'scan-look', strategyParams: {}, rules: [],
    },
  },
  {
    id: 'airport-hub',
    name: '机场枢纽',
    description: '大型枢纽，高层多梯，大客流（性能与大规模行为验证）。',
    setup: {
      floors: 60, floorHeight: 3.5, lobbyFloor: 0, elevatorCount: 24,
      elevator: { ...std, capacity: 20, ratedSpeed: 3.0 },
      segments: [{ startSec: 0, endSec: 3600, arrivalRatePer5min: 120 }],
      upPeak: 0.5, downPeak: 0.35, interfloor: 0.15,
      seed: 2026, strategyId: 'cost-group', strategyParams: {}, rules: [],
    },
  },
];

export function findTemplate(id: string): Template | undefined {
  return TEMPLATES.find((t) => t.id === id);
}

/** 场景 id → 中文名（展示/报告用）。 */
export function sceneLabel(id: string): string {
  return findTemplate(id)?.name ?? (id === 'custom' ? '自定义' : id);
}
